/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState } from 'react';
import { useTranslation as useTranslationUntyped } from 'react-i18next';
import { useAuthContext } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { apiService } from '../../services/api';
import styles from './EmailVerificationBanner.module.css';

const useTranslation = useTranslationUntyped as any;

/**
 * AB#1612 Phase 1A.2 — soft-gate banner for owners whose email isn't verified.
 *
 * Renders when AuthContext.emailVerificationRequired === true. Mounted in the
 * authenticated MainLayout so it's visible on every owner page until the
 * user clicks the link in their email and lands on /verify-email (which
 * calls markEmailVerified and clears the flag).
 *
 * Soft-gate (banner) per PO direction in wiki page 78 §"Reconciliation with
 * Phase 1A.2": login itself succeeds, branch/staff endpoints continue to
 * 403 with `email_verification_required` for sensitive actions. This banner
 * is the user's path to clearing both gates.
 */
const EmailVerificationBanner: React.FC = () => {
    const { t } = useTranslation('auth');
    const { showToast } = useToast();
    const {
        emailVerificationRequired,
        pendingVerificationEmail,
        markEmailVerified,
    } = useAuthContext();
    const [resending, setResending] = useState(false);
    const [checkingStatus, setCheckingStatus] = useState(false);

    if (!emailVerificationRequired) return null;

    const onResend = async () => {
        if (!pendingVerificationEmail) {
            showToast(t('signup.emailBanner.errors.noEmail'), 'error');
            return;
        }
        setResending(true);
        try {
            await apiService.requestEmailVerificationAuth({
                email: pendingVerificationEmail,
            });
            showToast(
                t('signup.emailBanner.toasts.resent', { email: pendingVerificationEmail }),
                'success',
            );
        } catch (err: any) {
            const status = err?.response?.status;
            if (status === 429) {
                showToast(t('signup.emailBanner.errors.rateLimited'), 'error');
            } else if (status === 423) {
                showToast(t('signup.emailBanner.errors.locked'), 'error');
            } else if (status === 401) {
                // Session expired mid-banner — let the global interceptor
                // handle it. Toast is just a hint.
                showToast(t('signup.emailBanner.errors.sessionExpired'), 'error');
            } else {
                showToast(t('signup.emailBanner.errors.generic'), 'error');
            }
        } finally {
            setResending(false);
        }
    };

    const onIveVerified = () => {
        // Optimistic clear — user clicks "I've verified" after acting on the
        // email link. Worst case the next backend 403 reveals it was
        // premature; banner re-appears on the next login. No API call here
        // because the verify-email landing page already calls verifyEmail
        // and toggles the flag. This button is for users who lost track
        // (already verified in another tab).
        setCheckingStatus(true);
        setTimeout(() => {
            markEmailVerified();
            setCheckingStatus(false);
        }, 200);
    };

    return (
        <div className={styles.banner} role="status" aria-live="polite">
            <span className={styles.icon} aria-hidden="true">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path
                        d="M12 9v4m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 0 0-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            </span>
            <div className={styles.body}>
                <strong className={styles.title}>{t('signup.emailBanner.title')}</strong>
                <span className={styles.message}>
                    {pendingVerificationEmail
                        ? t('signup.emailBanner.message', { email: pendingVerificationEmail })
                        : t('signup.emailBanner.messageNoEmail')}
                </span>
            </div>
            <div className={styles.actions}>
                <button
                    type="button"
                    className={styles.resendBtn}
                    onClick={onResend}
                    disabled={resending}
                >
                    {resending ? t('signup.emailBanner.resending') : t('signup.emailBanner.resend')}
                </button>
                <button
                    type="button"
                    className={styles.verifiedBtn}
                    onClick={onIveVerified}
                    disabled={checkingStatus}
                >
                    {t('signup.emailBanner.iveVerified')}
                </button>
            </div>
        </div>
    );
};

export default EmailVerificationBanner;
